import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridCustomCellProps, GridColumn as Column, GridCustomFooterCellProps, GridItemChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { formatNumber } from '@progress/kendo-intl';
import ChargeType from "./Cells/ChargeType";
import Description from "./Cells/Description";
import Quantity from "./Cells/Quantity";
import QuantityUoMCell from "./Cells/QuantityUoM";
import Rate from "./Cells/Rate";
import RateUoMCell from "./Cells/RateUoM";
import PayCommandCell from "./Cells/PayCommandCell";
import { PayRateLineItem as PayRateLineItemBase } from "TypeGen/Pay/pay-rate-line-item";
import { CalculatePayRateResult } from "TypeGen/Pay/calculate-pay-rate-result";
import { RateUnitOfMeasure } from "TypeGen/rate-unit-of-measure";
import { QuantityUnitOfMeasure } from "TypeGen/quantity-unit-of-measure";
import { ViewModel as ChargeTypeViewModel } from 'TypeGen/Order/ChargeTypes/view-model';
import { fetchApi } from "services/api";
import { useQuery } from "@tanstack/react-query";
import OrderCell from "./Cells/OrderCell";
import { plusIcon } from "@progress/kendo-svg-icons";
import { useCallback } from "react";

type PayRateLineItem = {
  // For Editing
  inEdit?: boolean;
  index?: number;
} & PayRateLineItemBase

type Props = {
  editable?: boolean | undefined;
  showComment?: boolean | undefined;
  data: CalculatePayRateResult;
  onChange?: (data: CalculatePayRateResult) => void;
  className?: string | undefined;
}

const PayEditable = ({ data, onChange, className, editable = true, showComment = true }: Props) => {

  const { data: chargeTypes = [] } = useQuery({
    queryKey: ['payChargeTypes'],
    queryFn: (): Promise<ChargeTypeViewModel[]> => fetchApi('/api/Order/GetPayChargeTypes'),
    staleTime: 60 * 1000
  });

  const itemChange = (event: GridItemChangeEvent) => {
    const newLineItems = data.LineItems
      .map((item, index) => index === event.dataItem.index
        ? { ...item, [event.field]: event.value }
        : item);

    // Fixup the Amount
    const newLineItem = newLineItems[event.dataItem.index];
    newLineItem.Amount = Math.round(((newLineItem.Rate * newLineItem.Quantity) + Number.EPSILON) * 100) / 100;

    // Fixup the Total
    const newTotal = newLineItems.reduce((total, item) => total + item.Amount, 0);

    onChange({ ...data, Total: newTotal, LineItems: newLineItems });
  }

  const itemRemove = ({ index }: PayRateLineItem) => {
    const newLineItems = data.LineItems.filter((_, idx) => idx !== index);

    // Fixup the Total
    const newTotal = newLineItems.reduce((total, item) => total + item.Amount, 0);

    onChange({ ...data, Total: newTotal, LineItems: newLineItems });
  }

  const allInEdit: Array<PayRateLineItem> = data.LineItems.map((item, index) =>
    Object.assign({ inEdit: editable, index }, item)
  );

  const CommandCell = (props: GridCustomCellProps) => (
    <PayCommandCell
      {...props}
      remove={itemRemove}
    />
  );

  const PayFooter = (props: GridCustomFooterCellProps) => {
    return (
      <td {...props.tdProps}>
        {formatNumber(data.Total, 'C')}
      </td>
    );
  };

  // Keep it stable, we don't want to re-rendering as we are typing
  const ChargeTypeCell = useCallback((props: GridCustomCellProps) => (
    <ChargeType {...props} chargeTypes={chargeTypes} />
  ), [chargeTypes]);

  const OrderNumberCell = useCallback((props: GridCustomCellProps) => (
    <OrderCell {...props} orderNumbers={data.OrderNumbers} />
  ), [data.OrderNumbers]);

  const DescriptionFooter = (props: GridCustomFooterCellProps, inEdit: boolean) => {
    return (
      <td {...props.tdProps}>
        {inEdit && <Button
          icon="add"
          svgIcon={plusIcon}
          themeColor="primary"
          onClick={() => onChange({
            ...data,
            LineItems: [
              ...data.LineItems,
              {
                DispatchOrderID: data.OrderNumbers[0]?.ID,
                BillingOrderID: 0, // Lookup in backend
                Rate: 0,
                RateUoM: RateUnitOfMeasure.Dollars,
                Quantity: 1,
                QuantityUoM: QuantityUnitOfMeasure.Flat,
                Description: "",
                Amount: 0,
                RateDescriptionID: 0,
              }
            ]
          })}
        >
          Add Line Item
        </Button>}
      </td>
    );
  };

  return <Grid
    data={allInEdit}
    onItemChange={itemChange}
    scrollable="none"
    className={className}
  >
    {showComment && <GridToolbar>
      <Button fillMode="link" disabled>{data.Comment}</Button>
    </GridToolbar>}
    <Column field="RateDescriptionID" title="Charge Type" cells={{ data: ChargeTypeCell, footerCell: (props) => DescriptionFooter(props, editable) }} />
    <Column field="Description" title="Description" cells={{ data: Description }} />
    {data.OrderNumbers.length > 1 && <Column field="DispatchOrderID" title="Order" width={140} cells={{ data: OrderNumberCell }} />}
    <Column field="Quantity" editor="numeric" width={120} cells={{ data: Quantity }} />
    <Column field="QuantityUoM" title="Quantity UoM" width={140} cells={{ data: QuantityUoMCell }} />
    <Column field="Rate" editor="numeric" width={120} cells={{ data: Rate }} />
    <Column field="RateUoM" title="Rate UoM" width={140} cells={{ data: RateUoMCell }} />
    <Column field="Amount" format="{0:c}" editable={false} width={80} cells={{ footerCell: PayFooter }} />
    {editable && <Column cells={{ data: CommandCell }} width={30} />}
  </Grid>
}

export default PayEditable;
